import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  KanbanSquare,
  Laptop2,
  Plus,
  ShieldCheck,
} from "lucide-react";
import {
  approveItIncident,
  assignItIncident,
  createItComment,
  createItIncident,
  finishItIncident,
  getItIncident,
  rejectItRequest,
  reorderItIncidents,
  reopenItIncident,
  startItIncident,
  updateItObservations,
  validateItIncident,
} from "../api/it";
import { WorkspaceHeader } from "@shared/components/WorkspaceHeader";
import { useGlobalNavigationTarget } from "@shared/navigation/globalNavigation";
import { ItCreateIncidentModal } from "../components/ItCreateIncidentModal";
import { ItDashboardPanel } from "../components/ItDashboardPanel";
import { ItDetailDrawer } from "../components/ItDetailDrawer";
import { ItTablaBoard } from "../components/ItTablaBoard";
import { ItKanbanBoard } from "../components/ItKanbanBoard";
import { ItToolbar, type ItFilters } from "../components/ItToolbar";
import { useIt } from "../hooks/useIt";
import {
  buildItGroups,
  getActiveReopenedCount,
  getVisibleItIncidents,
  getVisibleItGroupsByRole,
  type ItGroupBy,
  type ItIncident,
  type ItSortMode,
  type ItView,
} from "../types/it";
import "../styles/it.css";

const defaultFilters: ItFilters = {
  search: "",
  searchType: "todo",
  estado: "",
  proyecto: "",
  responsable: "",
  prioridad: "",
  categoria: "",
  onlyMine: false,
};

export function ItPage() {
  const { incidents, meta, loading, replaceIncident, setIncidents } = useIt();
  const [activeView, setActiveView] = useState<ItView>("tabla");
  const [groupBy, setGroupBy] = useState<ItGroupBy>("estado");
  const [sortMode, setSortMode] = useState<ItSortMode>("manual");
  const [filters, setFilters] = useState<ItFilters>(defaultFilters);
  const [createOpen, setCreateOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedIncidentId, setSelectedIncidentId] = useState<number | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);

  useGlobalNavigationTarget("it", (target) => {
    switch (target.section) {
      case "kanban":
      case "tabla":
      case "dashboard":
        setActiveView(target.section);
        break;
      default:
        break;
    }
  });

  const categories = useMemo(
    () =>
      Array.from(
        new Set([
          ...(meta.categories ?? []),
          ...incidents
            .map((incident) => incident.categoria?.trim())
            .filter((value): value is string => Boolean(value)),
        ]),
      ).sort((left, right) => left.localeCompare(right, "es")),
    [incidents, meta.categories],
  );

  const createIncidentMeta = useMemo(
    () => ({ ...meta, categories }),
    [categories, meta],
  );

  const filteredIncidents = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    const currentUserId = meta.current_user.id;

    return incidents.filter((incident) => {
      const searchText =
        filters.searchType === "codigo"
          ? incident.codigo
          : filters.searchType === "titulo"
            ? incident.titulo
            : [
                incident.codigo,
                incident.titulo,
                incident.descripcion,
                incident.usuario_alta_nombre,
                incident.responsable_nombre,
                incident.categoria,
                incident.proyecto_nombre,
              ]
                .filter(Boolean)
                .join(" ");

      const matchesQuery =
        query === "" || searchText.toLowerCase().includes(query);

      const matchesState =
        filters.estado === "" || incident.estado === filters.estado;

      const matchesProject =
        filters.proyecto === ""
          ? true
          : filters.proyecto === "none"
            ? incident.proyecto_id === null
            : String(incident.proyecto_id ?? "") === filters.proyecto;

      const matchesResponsible =
        filters.responsable === ""
          ? true
          : String(incident.responsable_id ?? "") === filters.responsable ||
            String(incident.usuario_alta_id) === filters.responsable;

      const matchesPriority =
        filters.prioridad === "" || incident.prioridad === filters.prioridad;

      const matchesCategory =
        filters.categoria === "" ||
        (incident.categoria ?? "").toLowerCase() ===
          filters.categoria.toLowerCase();

      const matchesMine =
        !filters.onlyMine || incident.responsable_id === currentUserId;

      return (
        matchesQuery &&
        matchesState &&
        matchesProject &&
        matchesResponsible &&
        matchesPriority &&
        matchesCategory &&
        matchesMine
      );
    });
  }, [filters, incidents, meta.current_user.id]);

  const visibleFilteredIncidents = useMemo(
    () => getVisibleItIncidents(filteredIncidents),
    [filteredIncidents],
  );

  const groups = useMemo(
    () =>
      buildItGroups(visibleFilteredIncidents, meta.projects, groupBy, sortMode),
    [visibleFilteredIncidents, meta.projects, groupBy, sortMode],
  );

  const visibleGroups = useMemo(
    () => getVisibleItGroupsByRole(groups, meta.current_user, groupBy),
    [groupBy, groups, meta.current_user],
  );

  const selectedIncident = useMemo(
    () =>
      incidents.find((incident) => incident.id === selectedIncidentId) ?? null,
    [incidents, selectedIncidentId],
  );

  const activeIncidents = visibleFilteredIncidents.filter((item) =>
    ["aprobada", "creada", "iniciada", "corregida"].includes(item.estado),
  ).length;

  const closedIncidents = visibleFilteredIncidents.filter(
    (item) => item.estado === "cerrada",
  ).length;

  const reopenedIncidents = getActiveReopenedCount(visibleFilteredIncidents);

  useEffect(() => {
    if (!selectedIncidentId) return;

    const timer = window.setInterval(() => {
      void getItIncident(selectedIncidentId)
        .then((incident) => replaceIncident(incident))
        .catch(() => {});
    }, 20000);

    return () => window.clearInterval(timer);
  }, [replaceIncident, selectedIncidentId]);

  const openIncident = (incident: ItIncident) => {
    setSelectedIncidentId(incident.id);
    setDrawerOpen(true);
  };

  const withBusy = async (runner: () => Promise<void>) => {
    setBusy(true);

    try {
      await runner();
    } finally {
      setBusy(false);
    }
  };

  const applyIncidentOrder = (source: ItIncident[], ids: number[]) =>
    source.map((incident) => {
      const index = ids.indexOf(incident.id);

      return index === -1 ? incident : { ...incident, orden_tabla: index + 1 };
    });

  const reorderGroup = async (ids: number[]) => {
    const previousIncidents = incidents;

    setIncidents((current) => applyIncidentOrder(current, ids));

    try {
      await reorderItIncidents(ids);
    } catch (error) {
      setIncidents(previousIncidents);
      throw error;
    }
  };

  return (
    <div className="page-shell it-page-shell !gap-4">
      <WorkspaceHeader
        eyebrow="Enterprise IT Operations"
        title="IT Workspace"
        description="Incidencias, validaciones, reaperturas, observaciones y seguimiento técnico desde una experiencia operativa clara."
        icon={Laptop2}
        loading={loading}
        metrics={[
          { label: "Total filtrado", value: visibleFilteredIncidents.length, icon: Laptop2 },
          { label: "Activas", value: activeIncidents, icon: Activity },
          { label: "Cerradas", value: closedIncidents, icon: ShieldCheck, tone: "emerald" },
          { label: "Reabiertas", value: reopenedIncidents, icon: KanbanSquare, tone: "amber" },
        ]}
        actions={
          meta.permissions.can_create ? (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[linear-gradient(135deg,#0b4a2f_0%,#0f5b3b_48%,#00a879_100%)] px-4 py-2.5 text-sm font-black text-white shadow-[0_16px_34px_rgba(11,74,47,.18)] xl:hidden"
            >
              <Plus size={15} />
              Nueva incidencia
            </button>
          ) : null
        }
      />

      <div className="it-workspace-grid min-w-0 max-w-full overflow-x-hidden">
        <section className="scroll-reveal-up flex min-w-0 max-w-full flex-col gap-3 overflow-x-hidden">
          <ItToolbar
            meta={meta}
            categories={categories}
            filters={filters}
            groupBy={groupBy}
            sortMode={sortMode}
            activeView={activeView}
            totalIncidents={visibleFilteredIncidents.length}
            activeIncidents={activeIncidents}
            reopenedCount={reopenedIncidents}
            onFiltersChange={(patch) =>
              setFilters((current) => ({ ...current, ...patch }))
            }
            onGroupByChange={setGroupBy}
            onSortModeChange={setSortMode}
            onViewChange={setActiveView}
            onOpenCreate={() => setCreateOpen(true)}
          />

          <div className="dashboard-scale-in min-w-0 overflow-x-hidden">
            {activeView === "kanban" && (
              <ItTablaBoard
                groups={visibleGroups}
                selectedIncidentId={selectedIncidentId}
                canReorder={
                  meta.permissions.can_reorder && sortMode === "manual"
                }
                onOpenIncident={openIncident}
                onReorder={reorderGroup}
              />
            )}

            {activeView === "tabla" && (
              <ItKanbanBoard
                groups={visibleGroups}
                canReorder={
                  meta.permissions.can_reorder && sortMode === "manual"
                }
                onOpenIncident={openIncident}
                onReorder={reorderGroup}
              />
            )}

            {activeView === "dashboard" && (
              <ItDashboardPanel
                incidents={visibleFilteredIncidents}
                allIncidents={filteredIncidents}
              />
            )}
          </div>
        </section>
      </div>

      <ItCreateIncidentModal
        open={createOpen}
        meta={createIncidentMeta}
        submitting={createBusy}
        onClose={() => setCreateOpen(false)}
        onSubmit={async (payload) => {
          setCreateBusy(true);

          try {
            const incident = await createItIncident(payload);

            replaceIncident(incident);
            setSelectedIncidentId(incident.id);
            setCreateOpen(false);
            setDrawerOpen(true);
            setActiveView("kanban");
          } finally {
            setCreateBusy(false);
          }
        }}
      />

      <ItDetailDrawer
        open={drawerOpen}
        incident={selectedIncident}
        meta={meta}
        busy={busy}
        onClose={() => setDrawerOpen(false)}
        onApprove={(incident) =>
          withBusy(async () => {
            replaceIncident(await approveItIncident(incident.id));
          })
        }
        onRejectRequest={(incident) =>
          withBusy(async () => {
            replaceIncident(await rejectItRequest(incident.id));
          })
        }
        onAssign={(incident, responsableId) =>
          withBusy(async () => {
            replaceIncident(await assignItIncident(incident.id, responsableId));
          })
        }
        onStart={(incident) =>
          withBusy(async () => {
            replaceIncident(await startItIncident(incident.id));
          })
        }
        onFinish={(incident, tiempoDedicado) =>
          withBusy(async () => {
            replaceIncident(
              await finishItIncident(incident.id, tiempoDedicado),
            );
          })
        }
        onValidate={(incident, action) =>
          withBusy(async () => {
            replaceIncident(await validateItIncident(incident.id, action));
          })
        }
        onReopen={(incident) =>
          withBusy(async () => {
            const reopenedIncident = await reopenItIncident(incident.id);

            replaceIncident(reopenedIncident);
            setSelectedIncidentId(reopenedIncident.id);
          })
        }
        onSaveObservation={(incident, type, value) =>
          withBusy(async () => {
            replaceIncident(
              await updateItObservations(incident.id, {
                tipo_observacion: type,
                observaciones: type === "solicitante" ? value : undefined,
                observaciones_responsable:
                  type === "responsable" ? value : undefined,
              }),
            );
          })
        }
        onSendComment={(incident, value) =>
          withBusy(async () => {
            const comment = await createItComment(incident.id, value);

            replaceIncident({
              ...incident,
              comentarios: [...incident.comentarios, comment],
            });
          })
        }
      />
    </div>
  );
}
